import { useAgentCopy } from '../../lib/agentCopy.js';
import { DigitalDocumentView } from '../../components/DigitalDocumentView.js';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {LIMITS,type DigitalDocument,type Locale,type SignatureAnchor,type SignatureDocument} from '@worki/shared';
import { api, RequestError } from '../../lib/api.js';
import { apiUrl, publicOrigin } from '../../lib/config.js';
import { useAuthenticated } from '../../lib/session.js';
import * as platform from '@platform';
import { currentLocale } from '../../i18n.js';
import { formatDateTime } from '../../lib/format.js';
import { sendActionOf } from '../../lib/sendAction.js';
import { Button, ErrorNotice, Field, Input, Textarea } from '../../components/ui.js';
import { Select } from '../../components/Select.js';
import { ConfirmDialog, Modal } from '../../components/Modal.js';
import { Icon } from '../../components/Icon.js';
import { SendDocumentCopyDialog } from '../../components/SendQuoteDialog.js';
import { useToast } from '../../components/Toast.js';
import { DocumentPages, ReplaceFile, StatusBadge } from './parts.js';
import { DigitalDocumentBuilder } from './DigitalDocumentBuilder.js';
import { DigitalDocumentPreview } from './DigitalDocumentPreview.js';

/**
 * SIG-004 — one document, and what can still be done to it.
 *
 * Everything here happens to a document that already exists, which is why the
 * placement panel points at the server's copy: once there is a row, its file
 * has an address, and that is the correct thing to draw. Making one is the
 * other screen's job.
 */

type Channel = 'email' | 'whatsapp';

interface SendResult {
  agentReview?: boolean;
  document: SignatureDocument;
  link: string;
  channel: Channel;
  delivered: boolean;
  /** WhatsApp only — the message, typed out, for the owner to press send on. */
  deepLink?: string;
}

export function DocumentDetail({
  document: initial,
  onClose,
  onChanged,
  agentReview = false,
}: {
  agentReview?: boolean;
  document: SignatureDocument;
  onClose: () => void;
  onChanged: () => void;
}) {
  const agentCopy = useAgentCopy();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const locale = currentLocale();
  const toast = useToast();
  const session = useAuthenticated();

  const [document, setDocument] = useState(initial);
  const [anchors, setAnchors] = useState<SignatureAnchor[]>(initial.anchors);
  const [format,setFormat] = useState<'link'|'pdf'|'both'>('link');
  const [channel, setChannel] = useState<Channel>('email');
  const [sendTo, setSendTo] = useState('');
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copying, setCopying] = useState(false);

  const changed = (next: SignatureDocument) => {
    setDocument(next);
    onChanged();
  };

  /**
   * The placements this panel last wrote down.
   *
   * A save answers with the list it was given, and that answer arrives after the
   * tap that followed it. Reading it back would take the screen backwards — the
   * box placed while the save was in the air would vanish, and the screen would
   * then agree with the server that it was never there. So an echo of our own
   * write is ignored, and anything else — a replaced file whose placements the
   * server dropped — still loads.
   */
  const written = useRef('');
  const stamp = (doc: SignatureDocument, list: SignatureAnchor[]) => `${doc.id}:${JSON.stringify(list)}`;

  useEffect(() => {
    if (stamp(document, document.anchors) === written.current) return;
    setAnchors(document.anchors);
  }, [document.id, document.anchors]);

  const editable = document.canEdit ?? (!document.isLocked && ['draft', 'sent', 'viewed'].includes(document.status) && !document.signedAt && !document.signers.some(signer => signer.signedAt));
  const dirty = editable && JSON.stringify(anchors) !== JSON.stringify(document.anchors);

  const savePlacements = useMutation({
    mutationFn: (list: SignatureAnchor[]) => {
      written.current = stamp(document, list);
      return api.put<SignatureDocument>(`/signatures/${document.id}/anchors`, { anchors: list, expectedRevision: document.contentRevision });
    },
    onSuccess: changed,
  });

  /**
   * Placements save themselves.
   *
   * There is no save button: a box put on a page is a decision, and asking for
   * it to be confirmed afterwards only creates a way to lose it. The wait keeps
   * a run of taps — place, nudge, resize — from becoming a run of requests, and
   * nothing is sent while a save is still in the air, so two of them cannot land
   * out of order and leave the older list on the document.
   */
  useEffect(() => {
    if (!dirty || savePlacements.isPending || savePlacements.isError) return;
    const timer = setTimeout(() => savePlacements.mutate(anchors), 600);
    return () => clearTimeout(timer);
  }, [anchors, dirty, savePlacements.isPending, savePlacements.isError]);

  /**
   * Closing keeps what was marked.
   *
   * Autosave waits a moment before writing, and closing is exactly the gesture
   * that beats it: mark a box, close, and the timer is torn down with the panel.
   * So the close does the save the timer had not got to, and a save that fails
   * leaves the panel open with the reason on it rather than closing over a loss.
   */
  const close = () => {
    if (dirty) {
      savePlacements.mutate(anchors, { onSuccess: onClose });
      return;
    }
    onClose();
  };

  /*
   * HIST 2.8 / 2.10 — which send this document is offered, if any: the first
   * send of a draft, a resend while it waits, a copy once it is signed, and
   * nothing once it has lapsed, been refused or been withdrawn. The same rule
   * the lists ask, so a document is not sendable here and closed there.
   */
  const action = sendActionOf('document', document);

  const send = useMutation({
    mutationFn: async () => {
      /*
       * The placements on screen go with the send, written or not. Autosave is
       * on a short timer and "mark a box, hit send" is fast enough to beat it —
       * sending a moment before the last box reached the server would have the
       * customer sign somewhere other than where the owner pointed.
       */
      if (dirty) {
        await api.put<SignatureDocument>(`/signatures/${document.id}/anchors`, { anchors, expectedRevision: document.contentRevision });
      }
      // A copy is not sent from here: it opens its own popup, which posts to the
      // copy route. This is the send, and the send route is the one that asks
      // for a signature.
      return api.post<SendResult>(`/signatures/${document.id}/send`, {
        channel,
        format,
        ...(sendTo.trim() ? { to: sendTo.trim() } : {}),
      });
    },
    onSuccess: (result) => {
      changed(result.document);
      setSendTo('');

      if (result.channel === 'whatsapp' && !result.delivered && result.deepLink) {
        void platform.openExternal(result.deepLink);
        toast.show(t('signing.whatsappReady'));
      } else {
        toast.success(result.delivered ? t('signing.sent') : t('signing.linkReady'));
        if (result.delivered) onClose();
      }
    },
  });

  const cancel = useMutation({
    mutationFn: () => api.post<SignatureDocument>(`/signatures/${document.id}/cancel`),
    onSuccess: changed,
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/signatures/${document.id}`),
    onSuccess: () => {
      setConfirmDelete(false);
      onChanged();
      onClose();
    },
  });

  /*
   * Whether there is still a request out to withdraw. A refusal closes the
   * document the way a signature does — both have been answered, and a withdrawn
   * one is withdrawn already. Sending is `action`'s question, not this one: a
   * lapsed document can still be withdrawn, but it is not sent again.
   */
  const open =
    document.status !== 'signed' && document.status !== 'cancelled' && document.status !== 'declined';
  const personalToken = document.signers.length <= 1 ? document.publicToken ?? document.signers[0]?.token : null;
  const link = personalToken
    ? `${publicOrigin((session as { publicBaseUrl?: string }).publicBaseUrl)}/sign/${personalToken}`
    : null;
  const signed = document.status === 'signed';

  return (
    <>
      <Modal
        open
        title={document.title}
        onClose={close}
        /* A page of a document is being read here, not a form filled in: the
           narrow sheet put an A4 page in a 448px column and the reader had to
           zoom to see where the signature box landed. */
        size="full"
        /* The status belongs beside the name of the document, not in a grid
           cell below it: it is the first thing anyone opening this panel is
           looking for, and a badge two lines under the title is a badge that
           gets read second. */
        headerActions={<StatusBadge status={document.status} />}
        footer={
          <>
            {agentReview && <Button variant="primary" onClick={close}>{agentCopy('continue')}</Button>}
            {!agentReview && (
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                {t('common.delete')}
              </Button>
            )}
            {!agentReview && <Button onClick={() => { onClose(); navigate(`/signing/new?duplicate=${document.id}`); }}>
              {t('signing.duplicateForAnotherSigner')}
            </Button>}
            {editable && <Button onClick={() => setEditing(true)}>{t('common.edit')}</Button>}
            {!agentReview && action && (
              <Button
                variant="primary"
                loading={send.isPending}
                /* 1.17 — a copy asks who it is for, in a popup, before it goes. */
                onClick={() => (action === 'copy' ? setCopying(true) : send.mutate())}
              >
                {action === 'send' ? t('signing.send') : action === 'copy' ? t('history.sendCopy') : t('signing.resend')}
              </Button>
            )}
          </>
        }
      >
        <div className="stack">
          <DocumentSummary document={document} locale={locale} />

          {/* The document comes before the sending details on purpose: on a
              draft the next thing to do is mark where the signature goes, and a
              panel below two form fields is a panel nobody scrolls to. */}
          {document.hasSource ? (
            <>
              <DocumentPages
                /* Once it is signed there is a better document to show: the copy
                   that was produced, signature and all. The original with empty
                   boxes on it is the question, and showing the question after it
                   has been answered reads as if nothing was signed. */
                src={
                  signed
                    ? apiUrl(`/signatures/${document.id}/pdf`)
                    : apiUrl(`/signatures/${document.id}/source.pdf`)
                }
                refreshKey={`${document.id}-${document.contentRevision}-${document.sourceName ?? ''}-${signed ? 'signed' : 'source'}`}
                /* The panel is the scroller here, so the pages are not capped
                   at 60vh inside a sheet that is already 92. Handing the wheel
                   back at the last page is the box's own behaviour now, rather
                   than something this one caller opts into. */
                boxClassName="max-h-none"
                anchors={signed ? [] : anchors}
                signerCount={document.signers.length}
                signerNames={Object.fromEntries(document.signers.map((signer) => [signer.position, signer.name]))}
                {...(editable ? { onAnchorsChange: setAnchors } : {})}
                label={editable ? t('signing.place') : t(signed ? 'signing.previewSigned' : 'signing.preview')}
                meta={[
                  document.sourceName,
                  // The count is the uploaded file's. The signed copy has the
                  // certificate page on the end, so saying it next to that copy
                  // would be one page short of what is on screen.
                  document.pageCount && !signed ? t('signing.filePages', { count: document.pageCount }) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />

              {editable && (
                <div className="row row--wrap">
                  {/* Nothing here to press: the marks are already on their way.
                      Said anyway, because a screen that saves silently and a
                      screen that forgot to save look the same. */}
                  <span className="muted">
                    {savePlacements.isPending || dirty ? t('signing.placeSaving') : t('signing.placeSaved')}
                  </span>
                  <ReplaceFile document={document} onReplaced={changed} />
                </div>
              )}

              {savePlacements.isError && <>
                <ErrorNotice code={(savePlacements.error as RequestError).code === 'errors.conflict' ? 'signing.revisionChanged' : (savePlacements.error as RequestError).code} />
                {(savePlacements.error as RequestError).code === 'errors.conflict' && <Button onClick={() => void api.get<SignatureDocument>(`/signatures/${document.id}`).then(next => { written.current = ''; changed(next); savePlacements.reset(); })}>{t('publicSign.reloadLatest')}</Button>}
              </>}
            </>
          ) : (
            /* A document written before SIG-003: still readable, still sendable,
               and shown as it always was. New ones cannot be typed. */
            <div className="stack">
              <span className="muted">{t('signing.body')}</span>
              <>{document.digitalDocument ? <DigitalDocumentView document={document.digitalDocument} values={document.fieldValues} /> : <p className="prewrap">{document.body}</p>}</>
            </div>
          )}

          {action && action !== 'copy' && (
            /* Two fields rather than one, because on a resend the destination is
               an override for this send — a decision, not a default — and it
               means a different thing on each channel. A copy has its popup
               instead, which asks for the recipient once, for both channels. */
            <div className="form-grid">
              <Field label={t('signing.sendVia')}>
                <Select
                  value={channel}
                  onChange={(value) => {
                    setChannel(value as Channel);
                    setSendTo('');
                  }}
                >
                  <option value="email">{t('signing.channelEmail')}</option>
                  <option value="whatsapp">{t('signing.channelWhatsapp')}</option>
                </Select>
              </Field>
              <Field label={t('signing.sendFormat')}><Select value={format} onChange={value=>setFormat(value as typeof format)}><option value="link">{t('signing.sendFormatLink')}</option><option value="pdf">PDF</option><option value="both">{t('signing.sendFormatBoth')}</option></Select></Field>
              <Field
                label={t(channel === 'email' ? 'signing.sendTo' : 'signing.sendToPhone')}
                hint={t(channel === 'email' ? 'signing.sendToHint' : 'signing.sendToPhoneHint')}
              >
                <Input
                  type={channel === 'email' ? 'email' : 'tel'}
                  dir="ltr"
                  value={sendTo}
                  placeholder={(channel === 'email' ? document.recipientEmail : document.recipientPhone) ?? ''}
                  onChange={(event) => setSendTo(event.target.value)}
                />
              </Field>
            </div>
          )}

          {/* SIG-003 — a link each, once there is more than one person to give
              one to. The document's own link is the first signer's, so showing
              only it would have the owner hand the same address to everybody:
              one signature, and the rest told it is already signed. */}
          {document.signers.length > 1 ? (
            <div className="stack">
              <span className="muted">{t('signing.signerLinks')}</span>
              {document.signers.map((signer) => (
                <Field
                  key={signer.position}
                  label={`${signer.name} · ${
                    signer.signedAt
                      ? t('signing.signedBy', {
                          name: signer.signerName ?? signer.name,
                          when: formatDateTime(signer.signedAt, locale),
                        })
                      : t('signing.signerWaiting')
                  }`}
                >
                  <Input
                    readOnly
                    dir="ltr"
                    value={
                      signer.token
                        ? `${publicOrigin((session as { publicBaseUrl?: string }).publicBaseUrl)}/sign/${signer.token}`
                        : ''
                    }
                    onFocus={(event) => event.target.select()}
                  />
                </Field>
              ))}
            </div>
          ) : (
            link && (
              <Field label={t('signing.link')}>
                {/* Read-only and selectable rather than a copy button alone: the
                    owner often needs to paste it into WhatsApp on the same phone. */}
                <Input readOnly dir="ltr" value={link} onFocus={(event) => event.target.select()} />
              </Field>
            )
          )}

          {/* The destination's own reason when there is one: a mistyped address,
              which "check the details" does not say. */}
          {send.isError && (
            <ErrorNotice code={(send.error as RequestError).fields?.to ?? (send.error as RequestError).code} />
          )}

          <div className="row row--wrap">
            {document.signedAt && (
              <Button
                icon={<Icon name="download" size={17} />}
                onClick={() => void api.download(`/signatures/${document.id}/pdf`, `${document.title}.pdf`)}
              >
                {t('signing.download')}
              </Button>
            )}
            {open && document.publicToken && (
              <Button variant="ghost" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                {t('signing.cancel')}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      {copying && (
        <SendDocumentCopyDialog open document={document} onClose={() => setCopying(false)} onSent={changed} />
      )}

      {editing && (
        <EditDialog
          document={document}
          onClose={() => setEditing(false)}
          onSaved={(next) => {
            changed(next);
            setEditing(false);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('signing.deleteTitle')}
        message={t('signing.deleteBody')}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

/**
 * Who the document went to, and where it stands.
 *
 * A component of its own so that what the owner is told can be rendered and
 * read without the dialog around it, which a server render cannot draw.
 *
 * One date, and it says what it is: the latest thing that happened to the
 * document. The panel used to date the day it was made, the send, the first
 * view and the signature or refusal, so whoever opened it to see where it
 * stands had to work out which of four dates was the answer. Each signer's own
 * date is still said, on the signers' list below, because that is another
 * person's answer rather than another date for this one.
 */
export function DocumentSummary({ document, locale }: { document: SignatureDocument; locale: Locale }) {
  const { t } = useTranslation();
  // Signing and refusing close the document, so at most one of them is set and
  // either is later than the send. One that was withdrawn or lapsed after going
  // out keeps its send date: the document holds no date for that, and the
  // badge already says it happened.
  const latest = document.signedAt
    ? t('signing.detailSigned', { when: formatDateTime(document.signedAt, locale) })
    : document.declinedAt
      ? t('signing.declinedAtLabel', { when: formatDateTime(document.declinedAt, locale) })
      : document.sentAt
        ? t('signing.sentAtLabel', { when: formatDateTime(document.sentAt, locale) })
        : t('signing.detailCreated', { when: formatDateTime(document.createdAt, locale) });

  return (
    <div className="rounded-xl border border-border bg-muted/30 p-4 grid gap-2 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <strong>{t('signing.numberBadge', { number: document.number })}</strong>
        <time className="ltr-nums">{latest}</time>
      </div>
      {/*
        Who it went to, on one line that wraps on a phone rather than a cell
        each. These are facts everyone already knows the shape of — a name, a
        phone number, an address — so they are simply said, in the order they
        are asked for. The dots between them are drawn by `.doc-summary`, at the
        start of each item after the first, so the item itself has to read the
        way the row does: a number that is left-to-right is isolated inside it,
        because an item that was left-to-right itself would have its start, and
        its dot, at the far side and the dots would sit one place off.
      */}
      <div className="doc-summary">
        <span className="doc-summary__name">{document.recipientName}</span>
        {[document.recipientPhone, document.recipientEmail].filter(Boolean).map((detail) => (
          <span key={detail} className="doc-summary__detail">
            <span className="ltr-nums" dir="ltr">
              {detail}
            </span>
          </span>
        ))}
      </div>
      {/*
        What is left is said quietly, in one line: whether it was opened and
        how often, which card it is linked to, and why it was refused.

        Whether it was opened is only said once the document has gone out:
        "not viewed yet" under a draft is not news about the recipient, it is a
        description of a document nobody has been sent. How often is a count
        and no more — when it was first opened, and for how long, were two more
        things to read and neither of them is what the owner came here to see.
      */}
      {(document.sentAt || document.customer || document.declineReason) && (
        <div className="doc-summary doc-summary--muted">
          {document.sentAt && (
            <span>
              {document.viewCount === 0
                ? t('signing.notViewed')
                : t('signing.viewedTimes', { count: document.viewCount })}
            </span>
          )}
          {document.customer && <span>{t('signing.linkedCustomer', { name: document.customer.name })}</span>}
          {document.declineReason && <span>{t('signing.declineReason', { reason: document.declineReason })}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * Editing an unsigned document with the same authoring surface as composition.
 *
 * The file and its placements are not here: replacing the file is done on the
 * document itself, where its pages are on screen to be marked again. What is
 * left is its title, recipient, and authored content when it has no source file.
 */
function EditDialog({
  document,
  onClose,
  onSaved,
}: {
  agentReview?: boolean;
  document: SignatureDocument;
  onClose: () => void;
  onSaved: (document: SignatureDocument) => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(document.title);
  const [body, setBody] = useState(document.body);
  const [digitalDocument, setDigitalDocument] = useState<DigitalDocument | null>(document.digitalDocument);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { business } = useAuthenticated();
  const locale = currentLocale();
  const [recipientName, setRecipientName] = useState(document.recipientName);
  const [recipientPhone, setRecipientPhone] = useState(document.recipientPhone ?? '');
  const [recipientEmail, setRecipientEmail] = useState(document.recipientEmail ?? '');
  const [additionalSigners,setAdditionalSigners]=useState(()=>document.signers.slice(1).map(s=>({name:s.name,phone:s.phone??'',email:s.email??''})));

  const save = useMutation({
    mutationFn: () =>
      api.patch<SignatureDocument>(`/signatures/${document.id}`, {
        expectedRevision: document.contentRevision,
        title: title.trim(),
        ...(document.hasSource ? {} : { body, digitalDocument }),
        recipientName: recipientName.trim(),
        recipientPhone: recipientPhone.trim() || null,
        recipientEmail: recipientEmail.trim() || null,
        additionalSigners:additionalSigners.map(s=>({name:s.name.trim(),phone:s.phone.trim()||null,email:s.email.trim()||null})),
      }),
    onSuccess: onSaved,
  });

  return (
    <>
    <Modal
      open
      size={digitalDocument ? "full" : "default"}
      title={t('signing.edit')}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          loading={save.isPending}
          disabled={!title.trim() || recipientName.trim().length < 2 || additionalSigners.some(s=>s.name.trim().length<2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email.trim()))}
          onClick={() => save.mutate()}
        >
          {t('common.save')}
        </Button>
      }
    >
      <div className="stack">
        <Field label={t('signing.documentName')} hint={t('signing.documentNameHint')}>
          <Input value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={t('signing.recipientName')} hint={t('signing.recipientHint')}>
            <Input value={recipientName} onChange={(event) => setRecipientName(event.target.value)} />
          </Field>
          <Field label={t('signing.recipientPhone')}>
            <Input type="tel" dir="ltr" value={recipientPhone} onChange={(event) => setRecipientPhone(event.target.value)} />
          </Field>
          <Field label={t('signing.recipientEmail')}>
            <Input type="email" dir="ltr" value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)} />
          </Field>
        </div>

        {additionalSigners.map((signer,index)=><div key={index} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label={t('signing.recipientName')}><Input value={signer.name} onChange={e=>setAdditionalSigners(all=>all.map((s,i)=>i===index?{...s,name:e.target.value}:s))}/></Field>
          <Field label={t('signing.recipientPhone')}><Input type="tel" dir="ltr" value={signer.phone} onChange={e=>setAdditionalSigners(all=>all.map((s,i)=>i===index?{...s,phone:e.target.value}:s))}/></Field>
          <Field label={t('signing.recipientEmail')}><Input type="email" dir="ltr" value={signer.email} onChange={e=>setAdditionalSigners(all=>all.map((s,i)=>i===index?{...s,email:e.target.value}:s))}/></Field>
        </div>)}
        <Button size="sm" disabled={additionalSigners.length>=LIMITS.maxSigners-1} onClick={()=>setAdditionalSigners(all=>[...all,{name:'',phone:'',email:''}])}>{t('signing.signerAdd')}</Button>

        {!document.hasSource && (digitalDocument
          ? <DigitalDocumentBuilder value={digitalDocument} onChange={setDigitalDocument} locale={locale} onPreview={() => setPreviewOpen(true)} />
          : <Field label={t('signing.body')}><Textarea value={body} onChange={event => setBody(event.target.value)} rows={10} /></Field>)}

        {save.isError && <>
          <ErrorNotice code={(save.error as RequestError).code === 'errors.conflict' ? 'signing.revisionChanged' : (save.error as RequestError).code} />
          {(save.error as RequestError).code === 'errors.conflict' && <Button onClick={() => void api.get<SignatureDocument>(`/signatures/${document.id}`).then(onSaved)}>{t('publicSign.reloadLatest')}</Button>}
        </>}
      </div>
    </Modal>
    {digitalDocument && previewOpen && <DigitalDocumentPreview document={digitalDocument} title={title} number={document.number ?? undefined} business={business} locale={locale} onClose={() => setPreviewOpen(false)} />}
    </>
  );
}
